/**
 * Les arrêts — ce qui empêche une ressource de rouler, et pourquoi.
 *
 * Tout ce qui est ici est une fonction pure. Le type `Indisponibilite` y vit
 * plutôt que dans `affectation.ts` : les deux fichiers en ont besoin, et le
 * poser du côté qui s'en sert le plus évite qu'ils s'importent l'un l'autre.
 *
 * **LA FAMILLE N'EST PAS UN PARAMÈTRE DU CLIENT.** Chacun choisit ses motifs —
 * un frigorifique aura « défaut de groupe froid », un déménageur autre chose —
 * mais personne ne choisit qu'un camion parte en congés. Deux familles, pas une
 * de plus : `personnel` pour les chauffeurs, `materiel` pour les véhicules et
 * les remorques. Elle est DÉDUITE de la ressource et jamais saisie ; l'API la
 * déduit de son côté, et la clé étrangère à trois colonnes la vérifie en base.
 */

import { couvre, enFrancais, type Affectation } from './affectation.js';

/** Sur quoi porte un arrêt. */
export type Genre = 'chauffeur' | 'vehicule' | 'remorque';

/** À quelle famille de motifs cette ressource donne droit. */
export type Famille = 'personnel' | 'materiel';

/** Un arrêt, tel que l'API le rend. */
export interface Indisponibilite {
  readonly id: string;
  readonly chauffeur_id: string | null;
  readonly vehicule_id: string | null;
  readonly remorque_id: string | null;
  readonly genre: Genre;
  /** Le nom du chauffeur, ou l'immatriculation du véhicule ou de la remorque. */
  readonly ressource: string;
  readonly motif_id: string;
  readonly motif: string;
  readonly famille: Famille;
  readonly du: string;
  readonly au: string | null;
  readonly note: string | null;
}

/** Un motif, propre à la société. */
export interface Motif {
  readonly id: string;
  readonly nom: string;
  readonly famille: Famille;
  readonly actif: boolean;
}

/** La famille dont relève un genre de ressource. */
export function familleDe(genre: Genre): Famille {
  return genre === 'chauffeur' ? 'personnel' : 'materiel';
}

/** La colonne d'identité que porte un arrêt sur ce genre de ressource. */
export function colonneDe(
  genre: Genre,
): 'chauffeur_id' | 'vehicule_id' | 'remorque_id' {
  if (genre === 'chauffeur') return 'chauffeur_id';
  if (genre === 'vehicule') return 'vehicule_id';
  return 'remorque_id';
}

/** « chauffeur », « véhicule », « remorque » — pour être lu. */
export function genreDit(genre: Genre): string {
  return genre === 'vehicule' ? 'véhicule' : genre;
}

// ---------------------------------------------------------------------------
// Trier, filtrer, dire
// ---------------------------------------------------------------------------

/**
 * Un arrêt est-il EN COURS au jour dit ?
 *
 * Même règle que pour un attelage : en cours veut dire « couvre ce jour », et
 * non « sans fin ». Un arrêt programmé pour la semaine prochaine n'a pas de fin
 * non plus.
 */
export function enCours(arret: Indisponibilite, jour: string): boolean {
  return couvre(arret, jour);
}

/** Passé, en cours, ou à venir — par rapport au jour dit. */
export type Moment = 'passe' | 'en_cours' | 'a_venir';

export function moment(arret: Indisponibilite, jour: string): Moment {
  if (arret.du > jour) return 'a_venir';
  if (arret.au !== null && arret.au < jour) return 'passe';
  return 'en_cours';
}

/**
 * Les arrêts rangés : ceux en cours d'abord, puis ceux à venir, puis les
 * passés — et à l'intérieur de chaque groupe, du plus récent au plus ancien.
 *
 * L'API rend déjà la liste du plus récent au plus ancien ; ce rangement-ci
 * n'est pas un second tri mais un REGROUPEMENT. Sans lui, un arrêt clos en
 * janvier passe devant celui qui court aujourd'hui, parce qu'il a commencé
 * plus tard.
 */
export function ranger(
  arrets: readonly Indisponibilite[],
  jour: string,
): readonly Indisponibilite[] {
  const rang: Readonly<Record<Moment, number>> = { en_cours: 0, a_venir: 1, passe: 2 };
  return [...arrets].sort((a, b) => {
    const ecart = rang[moment(a, jour)] - rang[moment(b, jour)];
    if (ecart !== 0) return ecart;
    return b.du.localeCompare(a.du);
  });
}

/** Les motifs qu'on peut choisir pour cette famille : les ACTIFS seulement. */
export function motifsDe(motifs: readonly Motif[], famille: Famille): readonly Motif[] {
  return motifs.filter((m) => m.famille === famille && m.actif);
}

/**
 * « du 01/09/2026 au 10/09/2026 », ou « depuis le 01/09/2026 ».
 *
 * Distincte de `periodeDite` d'`affectation.ts` sur un point : un arrêt SANS
 * FIN qui n'a pas commencé se dit « à partir du », et non « depuis le ». Dire
 * « depuis » d'un congé qui débute lundi prochain serait faux.
 */
export function quand(arret: Indisponibilite, jour: string): string {
  const debut = enFrancais(arret.du) ?? arret.du;
  if (arret.au !== null) {
    return `du ${debut} au ${enFrancais(arret.au) ?? arret.au}`;
  }
  return arret.du > jour ? `à partir du ${debut}` : `depuis le ${debut}`;
}

// ---------------------------------------------------------------------------
// La saisie
// ---------------------------------------------------------------------------

/** Ce que le formulaire tient. Les dates sont en `AAAA-MM-JJ`. */
export interface SaisieArret {
  readonly genre: Genre;
  readonly ressourceId: string;
  readonly motifId: string;
  readonly du: string;
  readonly au: string;
  readonly note: string;
}

export function saisieVide(jour: string): SaisieArret {
  return {
    genre: 'chauffeur',
    ressourceId: '',
    motifId: '',
    du: jour,
    au: '',
    note: '',
  };
}

/**
 * Contrôle la saisie AVANT l'envoi. Ce contrôle ANTICIPE celui de l'API, il ne
 * le remplace pas : si les deux divergent, c'est l'API qui a raison.
 *
 * Ce qu'il attrape, et que l'API attraperait aussi mais dans un bandeau plutôt
 * que sous le champ : la ressource et le motif manquants, une fin antérieure au
 * début. **La fin reste facultative** — un arrêt dont on ne connaît pas la fin
 * est le cas ordinaire, décidé le 03/09/2026.
 */
export function validerArret(
  saisie: SaisieArret,
  motifs: readonly Motif[],
): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  if (saisie.ressourceId === '') {
    fautes['ressourceId'] =
      `Choisissez ${saisie.genre === 'chauffeur' ? 'un chauffeur' : saisie.genre === 'vehicule' ? 'un véhicule' : 'une remorque'}.`;
  }

  if (saisie.motifId === '') {
    fautes['motifId'] = 'Choisissez un motif.';
  } else {
    // Un motif de la mauvaise famille serait refusé par la base, par une
    // violation de clé étrangère que personne ne lit. On le dit ici.
    const motif = motifs.find((m) => m.id === saisie.motifId);
    if (motif !== undefined && motif.famille !== familleDe(saisie.genre)) {
      fautes['motifId'] = `« ${motif.nom} » ne s’applique pas à ${
        saisie.genre === 'chauffeur' ? 'un chauffeur' : 'du matériel'
      }.`;
    }
  }

  if (saisie.du === '') {
    fautes['du'] = 'La date de début est obligatoire.';
  } else if (saisie.au !== '' && saisie.au < saisie.du) {
    fautes['au'] = 'La fin ne peut pas précéder le début.';
  }

  return fautes;
}

/** Ce qu'on envoie à la création : la ressource sous sa seule colonne. */
export function pourCreation(saisie: SaisieArret): Readonly<Record<string, string>> {
  const corps: Record<string, string> = {
    [colonneDe(saisie.genre)]: saisie.ressourceId,
    motif_id: saisie.motifId,
    du: saisie.du,
  };
  if (saisie.au !== '') corps['au'] = saisie.au;
  if (saisie.note.trim() !== '') corps['note'] = saisie.note.trim();
  return corps;
}

// ---------------------------------------------------------------------------
// Ce qu'un arrêt empêche
// ---------------------------------------------------------------------------

/**
 * Cette ressource est-elle ATTELÉE pendant tout ou partie de l'arrêt qu'on
 * s'apprête à poser ?
 *
 * **Ce n'est pas un refus** : la base accepte parfaitement d'immobiliser une
 * remorque attelée, et c'est ce qu'on veut — une panne ne demande la permission
 * à personne. Mais poser un arrêt sans voir que le camion part demain avec
 * serait une mauvaise surprise. L'écran prévient, et laisse enregistrer.
 */
export function attelagesGenants(
  affectations: readonly Affectation[],
  saisie: SaisieArret,
): readonly Affectation[] {
  if (saisie.genre === 'vehicule' || saisie.ressourceId === '' || saisie.du === '') {
    // Un arrêt de véhicule ne gêne aucun attelage : c'est le véhicule qui
    // PORTE l'attelage, et l'immobiliser n'en détache rien.
    return saisie.genre === 'vehicule'
      ? affectations.filter((a) => a.vehicule_id === saisie.ressourceId)
      : [];
  }

  const colonne = saisie.genre === 'chauffeur' ? 'chauffeur_id' : 'remorque_id';
  const fin = saisie.au === '' ? null : saisie.au;

  return affectations.filter((a) => {
    if (a[colonne] !== saisie.ressourceId) return false;
    // Deux périodes se chevauchent si chacune commence avant la fin de l'autre.
    const apresLaFin = fin !== null && a.du > fin;
    const avantLeDebut = a.au !== null && a.au < saisie.du;
    return !apresLaFin && !avantLeDebut;
  });
}

/** « GZ-083-NF, du 01/08/2026 » — de quoi reconnaître l'attelage gênant. */
export function attelageDit(attelage: Affectation): string {
  return `${attelage.vehicule}, ${
    attelage.au === null
      ? `depuis le ${enFrancais(attelage.du) ?? attelage.du}`
      : `du ${enFrancais(attelage.du) ?? attelage.du} au ${enFrancais(attelage.au) ?? attelage.au}`
  }`;
}

/**
 * Ce qu'on peut faire à un arrêt, selon là où il en est.
 *
 * DEUX PIÈGES, tous deux vus à l'écran le 03/09/2026 :
 *
 *   - **clore un arrêt qui n'a pas commencé** poserait une fin antérieure au
 *     début. La base refuse — un intervalle dont la borne haute précède la
 *     basse n'existe pas — par une erreur que personne ne lit. Un arrêt
 *     programmé ne se clôt pas : il se supprime ;
 *   - **« Rouvrir » sur un arrêt EN COURS** dit autre chose que ce qu'il fait.
 *     L'opération est la même — retirer la fin — mais « rouvrir » laisse
 *     entendre que l'arrêt était terminé. Le bouton dit donc ce qu'il fait.
 */
export interface Commandes {
  /** Poser la fin à la veille du jour où l'on regarde. */
  readonly clore: boolean;
  /** Retirer la fin. Le mot change selon que l'arrêt est fini ou non. */
  readonly retirerLaFin: false | 'Rouvrir' | 'Retirer la fin';
}

export function commandes(arret: Indisponibilite, jour: string): Commandes {
  if (arret.au === null) {
    return { clore: arret.du <= jour, retirerLaFin: false };
  }
  return {
    clore: false,
    retirerLaFin: moment(arret, jour) === 'passe' ? 'Rouvrir' : 'Retirer la fin',
  };
}

/** Le décompte de la liste : « 3 arrêts — dont 1 en cours ». */
export function decompte(arrets: readonly Indisponibilite[], jour: string): string {
  const total = arrets.length;
  const debut = `${total} arrêt${total > 1 ? 's' : ''}`;
  const courants = arrets.filter((a) => enCours(a, jour)).length;
  if (courants === 0) return debut;
  return `${debut} — dont ${courants} en cours`;
}
